import { InferType, array, boolean, number, object, string } from 'yup'

import {
  ACTIVITY_LEVELS,
  CORPULENCE_LEVELS,
  GENDER_FEMALE,
  GENDER_MALE,
} from './constants'

export enum NamespaceType {
  public = 'public',
  private = 'private',
}

export enum Members {
  thomas = 'thomas',
  vet = 'vet',
}

export type namespaceConstantType = [string, string]

export type corpulenceValues =
  typeof CORPULENCE_LEVELS[keyof typeof CORPULENCE_LEVELS]
export type activityLevelValues =
  typeof ACTIVITY_LEVELS[keyof typeof ACTIVITY_LEVELS]
export type genderValues = typeof GENDER_MALE | typeof GENDER_FEMALE

export enum StatusType {
  unauthenticated = 'unauthenticated',
  authenticated = 'authenticated',
}

export enum SpecificityType {
  sickness = 'sickness',
  allergy = 'allergy',
  sensibility = 'sensibility',
  gluttony = 'gluttony',
  old_kibble = 'old_kibble',
  old_wet_food = 'old_wet_food',
  change_reason = 'change_reason',
}

export enum RoleType {
  japhytos = 'japhytos',
  customer = 'customer',
  admin = 'admin',
  logistic = 'logistic',
}

export enum PetType {
  oneCat = 'one_cat',
  oneDog = 'one_dog',
  multiplePets = 'multiple_pets',
}

export interface Role {
  createdAt: string
  description: string
  id: number
  name: RoleType
  policies: any[]
  updatedAt: string
}

export enum RecipeFlavor {
  fish = 'fish',
  beef = 'beef',
  chicken = 'chicken',
}

export enum GrowthStage {
  baby = 'baby',
  adult = 'adult',
  senior = 'senior',
  superSenior = 'super_senior',
}

export enum ItemType {
  standardisedKibble = 'standardised_kibble_recipe',
  kibble = 'kibble_recipe',
  wetfood = 'wetFood_recipe',
  wetfoodLegacy = 'wetFood',
  upsell = 'upsell',
  gift = 'gift',
  goodie = 'goodie',
}

export enum ServiceType {
  upsell = 'upsell',
  customFood = 'custom_food',
  goodie = 'goodie',
}

export enum DiscountType {
  percentage = 'percentage',
  fixedAmount = 'fixedAmount',
}

export enum PricingType {
  base = 'base',
  baseWithPromotion = 'baseWithPromotion',
  trialKilogram = 'trialKilogram',
  trial = 'trial',
  monthly = 'monthly',
}

export type Price = {
  amount: number
  currency: string
}

export type Pricings = Partial<{
  [key in PricingType]: Price
}>

export interface PromoCode {
  code: string
  countdown: number
  creationDate: string
  description: string
  effects: any
  endAt: string
  id: number
  imageUrl: string
  initialCountdown: number
  promoType: string
  startAt: string
  type: string
}

export interface PickupPoint {
  id: number
  externalId: string
  address: string
  additionalAddress: string
  thirdAddress: string
  city: string
  postalCode: string
  countryCode: string
  name: string
  distance: number
  coord: { lat: number; lng: number }
  openingHours: {
    monday: string
    tuesday: string
    wednesday: string
    thursday: string
    friday: string
    saturday: string
    sunday: string
  }
  mobilityEnabled: boolean
  type: string
  carrier: string
  maxWeight: number
}

export type BillingContact = {
  additionalAddress: string
  address: string
  carrierPreference: string
  city: string
  companyName: string
  countryCode: string
  firstName: string
  id: number
  lastName: string
  phoneNumber: string
  pickupPoint: PickupPoint
  postalCode: string
}

export type DeliveryContact = {
  additionalAddress: string
  address: string
  carrierPreference: string
  city: string
  companyName: string
  countryCode: string
  firstName: string
  email: string | null
  id: number
  lastName: string
  phoneNumber: string
  pickupPoint: PickupPoint
  postalCode: string
}

export const petSchema = object({
  birth: object().defined(),
  id: number().required().integer(),
  customerId: number().nullable().integer(),
  breeds: array().min(1).defined(),
  resourceId: string(),
  averageAdultWeight: number().integer(),
  birthdayDay: string(),
  wetFoodRatio: string().optional().nullable(),
  flags: object().optional(),
  name: string().required(),
  diet: string().optional(),
  species: string().required(),
  specificities: array()
    .of(
      object({
        id: number(),
        type: string(),
        label: string(),
        description: string(),
        data: object(),
      }).required(),
    )
    .required(),
  gender: string().required(),
  growthStage: string().nullable(),
  isSpayed: boolean().defined(),
  weight: number().required().positive().integer(),
  serviceIds: array().of(string()),
  activityLevel: number().required().positive().integer(),
  corpulenceLevel: number().required().positive().integer(),
  status: string(),
})

export interface Breed {
  id: number
  label: string
  size: {
    id: number
    name: string
  }
  species: {
    id: number
    name: string
  }
  genderSpecificities: {
    male: {
      averageAdultWeight: number
      ageThresholds: {
        adult: number
        senior: number
        superSenior: number
      }
    }
    female: {
      averageAdultWeight: number
      ageThresholds: {
        adult: number
        senior: number
        superSenior: number
      }
    }
  }
  nordic: string
  isSelectable: string
  obesity: string
  thinness: string
  recommendedFlavor: string
  petFedCount: number
  energyCoefficient: number
  metadata: {
    illustration: string
  }
}

export interface Pet extends InferType<typeof petSchema> {
  activityLevel: activityLevelValues
  gender: genderValues | null
  breeds: Breed[]
  dailyEnergyRequirement?: number
  ageInMonths?: number
}

export interface Rule {
  id: number
  resourceId?: number
  weight: number
  articles: any
  isActive: string
  createdAt: string
  updatedAt: string
}

export interface Recipe {
  id: number
  article: any
  articleId: number
  ingredients: string[]
  label: string
  marketLabel: string
  metaboEnergy: number
  reference: string
  species: string
  diameter: number
  rules: Rule[]
  templateFile: string
  imageUrls: any
}

export interface Benefit {
  id: number
  name: string
  metadata: any
}

export interface Item {
  id: number
  promoCodeId?: number
  type: ItemType
  article: any
  articles: any
  recipe: any
  productId: number
  quantity: any
  pricing: Pricings
  imageUrls: any
  isNew: boolean
  translationKeys: any
  metaInformation?: any
  deliveryFrequency?: number
  serviceId?: number
  petId?: number
}

export interface Service {
  id: number
  petId: number
  deliveryFrequency?: number
  subscriptionId: number
  type: ServiceType
  nextBillingDate?: string
  expectedDeliveryDateRange?: [Date, Date]
  catchphrase: string
  status: string
  pricing: Pricings
  items: Item[]
  createdAt?: string
  updatedAt?: string
}

export interface Prospect {
  firstName: string
  lastName: string
  email: string
  address: string
  additionalAddress: string
  city: string
  company: string
  postalCode: string
  country: string
  phoneNumber: string
  pointRelayAddress: any
  promoCode: PromoCode
  registrationCode: PromoCode
}

export interface Customer {
  id: number
  email: string
  firstName: string
  lastName: string
  phoneNumber: string
  billingContact: BillingContact
  deliveryContact: DeliveryContact
  discount: any
  nextBillingDate: string
  nextDeliveryDate: string
  nextDebitDate: string
  registrationCode: PromoCode
  sponsorshipCode: PromoCode
  shippingFees: any
  shippingFeesWithoutPromotion: any
  subscriptionStatus: string
  statistics: any
  registered: false
  registrationDate: string
  unsubscribeDateAble: string
  flags: any
  totalMonthlyPrice: any
  totalSubscriptionPrice: any
  totalSubscriptionPriceWithoutPromotion: any
  totalMonthlyPriceWithoutPromotion: any
}

export interface LightDiscount {
  freeShipping: boolean
  kibblePercent: number
}

export interface Commitment {
  id: number
  subscriptionId: number
  commitmentId: number
  orderQuantityInitialCountdown: number
  orderQuantityCountdown: number
  counterpart: any
  isRenewalActive: boolean
  version: number
  commitedAt: string
  completedAt: string
}

export interface Subscription {
  id: number
  customerId: number
  paymentMethodId: number
  deliveryFrequency: number
  recommendedDeliveryFrequency: number
  nextDeliveryDate: string
  nextBillingDate: string
  lastShippingDate: string
  isCancellationScheduled: boolean
  shippingFees: any
  nextBillingOffset: number
  nextDebitDate: string
  isTrial: string
  isUnpaid: string
  pricingVersion: number
  multiPetDiscountVersion?: number
  createdAt: string
  updatedAt: string
  services: Service[]
  discount: LightDiscount
  shippingFeesVersion: number
  shippingFeesType: string
  pricing: Pricings
  commitementVersion: number
  activeCommitment: Commitment
}

export type DiscountReason =
  | 'godfather'
  | 'commitment'
  | 'godson'
  | 'giftCard'
  | 'resubscribe'
  | 'acquisition_offer'
  | 'agentPartialReimbursement'
  | 'agentCommercialGesture'
  | ''

export interface Discount {
  id: number
  customerId: number
  type: DiscountType
  amount: number
  initialCountdown: number
  countdown: number
  isCumulative: string
  target: string
  conditions: any[]
  reason: DiscountReason
  createdAt: string
  updatedAt: string
  isDeleted?: 'yes' | 'no'
}

export interface Order {
  id: number
  customerId: number
  pets: null
  type: string
  status: string
  carrierPreference: string
  billingContact: BillingContact
  deliveryContact: DeliveryContact
  products: any[]
  items: any[]
  priority: number
  note: string
  packages: any
  duration: number
  expectedShippingDate: string
  expectedDeliveryDateRange: string
  shippingDate: any
  expectedDeliveryDate: string
  createdAt: string
  updatedAt: string
  transaction: any
}

export interface NextOrder {
  id: number
  customerId: number
  petIds: number[]
  serviceIds: number[]
  email: string
  carrierPreference: string
  type: string
  externalId: any
  partnerId: number
  status: string
  billingContact: BillingContact
  deliveryContact: DeliveryContact
  deliveryFee: number
  priority: number
  note: string
  expectedShippingDate: Date
  expectedDeliveryDateRange: [Date, Date]
  shippingDate: any
  weight: number
  TransactionId: number
  createdAt: Date
  updatedAt: Date
  items: any[]
  packages: any[]
  synchronizedAt: Date
  pets: any[]
  products: any[]
  expectedDeliveryDate: Date
  transaction: any
}

export type DiscountCollection = {
  multiPetDiscounts: Discount[]
  postRegistration: Discount[]
  registration: Discount
}

export enum FlowType {
  classic = 'classic-mode',
  creation = 'creation-mode',
  // TODO : Remove after ab testing on transition pages finished
  transition = 'transition-mode',
  update = 'updating-mode',
}

export enum PetCountFlowType {
  single = 'single',
  two = 'two',
  infinite = 'infinite',
}

export interface PetItems {
  [petId: number]: Item[]
}

export interface Category {
  id: number
  type: string
  subtype: string
  label: string
  visibleToCustomer: boolean
  rank: number
}

export interface Packing {
  ArticleID: number
  PackArticleID: number
  Size: number
}

export interface Article {
  packings: Packing[]
  id: number
  label: string
  isSaleable: boolean
  pcb: number
  saleUnit: string
  supplier: number
  type: string
  weightUnit: string
  weight: number
  createdAt: Date
  updatedAt: Date
}

export interface Gift {
  article: Article
  countdown: number
  customerID: number
  id: number
  initialCountDown: number
  quantity: number
  reason: string
  createdAt: Date
  updatedAt: Date
}

export interface PaymentMethod {
  id: number
  customerId: number
  provider: string
  providerUserId: string
  providerPaymentMethodId: string
  expiration: string
  lastNumber: string
  createdAt: Date
  updatedAt: Date
}

export interface Transaction {
  id: number
  orderId: number
  subscriptionId: number
  customerId: number
  context: string
  externalId: string
  paymentProvider: string
  status: string
  price: {
    amount: number
    currencyCode: string
    vat: number
  }
  attemptCount: number
  nextAttemptAt: Date | null
  details: {
    discountAmount: {
      amount: number
      currencyCode: string
      vat: number
    }
    shippingFees: {
      amount: number
      currencyCode: string
      vat: number
    }
    orderId: number
    serviceIds: number[]
  }
  paidAt: Date
  createdAt: Date
  updatedAt: Date
  contact: any
}

export interface Note {
  id: number
  customerID: number
  comment: string
}

export interface Call {
  createdAt: Date
  updatedAt: Date
  string
  customerId: number
  id: number
  note: string
  result: string
  reasons: any[]
}

export interface Reason {
  id: number
  name: string
  label: string
  translationKey: string
  type: string
}

export interface SubscriptionDiscount {
  activeMultiPetDiscount: number
  multiPetDiscounts: any[]
}

export interface Log {
  id: number
  customerId: number
  who: string
  whoId: number
  what: string
  whatId: number
  action: string
  reason: {
    id: number
    type: string
    translationKey: string
    label: string
  }
  details: string
  data: null
  createdAt: Date
}

export enum FilterType {
  CHECKBOX = 'checkbox',
  DATE = 'date',
  GROUP = 'group',
  DATE_PICKER = 'date-picker',
  TEXT = 'text',
  TEXT_INPUT = 'text-input',
}
export interface Filter {
  key: string
  type?: FilterType
  label: string
  value?: string
  placeholder?: string
  disabled?: boolean
  children?: Filter[] | ((items: any) => Filter[])
  condition?: (item: any, filter: Filter) => boolean
  onToggle?: (value: string, isActivated: boolean) => void
}

export interface SortOption {
  label: string
  value: string
  sortFunction: (a: any, b: any) => number
}

export interface PaginationMetadata {
  resultCount: number
  previousOffset: number
  nextOffset: number
}

export interface Effect {
  id: number
  type: string
  label: string
  data: any
  conditions?: any
}

export interface Specificity {
  id: number
  type: SpecificityType
  label: string
  description: string
  data: any
}

export interface ShippingFeesVersion {
  ID: number
  IsCurrent: string
  IsAvailableOnSubscription: string
  IsAvailableOnSwitch: string
  CreatedAt: Date
  UpdatedAt: Date
}

export enum KibbleSubFamily {
  beef = 'beef',
  chicken = 'chicken',
  fish = 'fish',
}

export enum SpecificitiesSensitivityType {
  SensitiveIrritatedSkin = 'sensitive_irritated_skin',
  DullCoat = 'dull_coat',
  Itching = 'itching',
  UrinaryDiscomfort = 'urinary_discomfort',
  InvasiveHairballs = 'invasive_hairballs',
  Tartar = 'tartar',
  BadBreath = 'bad_breath',
  FoodIntolerances = 'food_intolerances',
  DigestiveSensitivity = 'digestive_sensitivity',
  Default = 'default',
}

export enum SpecificitiesChangeReasonType {
  StimulateAppetite = 'stimulate_appetite',
  HealthierFood = 'healthier_food',
  WeightLoss = 'weight_loss',
  ImproveDigestion = 'improve_digestion',
  HealthySkinCoat = 'healthy_skin_coat',
  MonthlyDelivery = 'monthly_delivery',
  Other = 'other',
}

export enum Species {
  Dog = 'dog',
  Cat = 'cat',
}

export enum BodyStages {
  Baby = 'baby',
  Slim = 'slim',
  Slight = 'slight',
  Thick = 'thick',
}

export enum TransitionContent {
  Before = 'before',
  After = 'after',
  Description = 'description',
}

export enum ReviewBlockContent {
  Image = 'image',
  Title = 'title',
  Description = 'description',
  Name = 'name',
}

export type TransitionItem = {
  [key in TransitionContent]: string
}

export type ReviewBlockField = {
  [key in ReviewBlockContent]: string
}

export type TransitionFatnessContent = {
  [key in Species]: {
    [key in BodyStages]: TransitionItem
  }
}

export type TransitionSensibilityContent = {
  // Here we only include the specificities that we have for the moment we will populate this later
  [key in Species]: {
    [key in
      | SpecificitiesSensitivityType.Default
      | SpecificitiesSensitivityType.DigestiveSensitivity]: TransitionItem
  }
}

export type TransitionChangeReasonContent = {
  [key in Species]: {
    [key in SpecificitiesChangeReasonType]: ReviewBlockField[]
  }
}
